import React, { useState, useEffect } from 'react';
import { ModernSidebar } from './ModernSidebar';
import { MobileNavigation } from './MobileNavigation';
import { Breadcrumbs } from './Breadcrumbs';
import { GlobalSearch } from './GlobalSearch';
import { Clock, AlertTriangle, Moon, Sun, ArrowLeft, GraduationCap, Languages } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useInstructor } from '../../hooks/useInstructor';
import { useInterpreter } from '../../hooks/useInterpreter';
import { useClient } from '../../hooks/useClient';
import { useSuperAdmin } from '../../hooks/useSuperAdmin';
import { useTheme } from '../../hooks/useTheme';
import { useNavigate } from 'react-router-dom';
import { NotificationBell } from '../admin/NotificationBell';
import { TimeWeatherWidget } from './TimeWeatherWidget';
import { InlineOfflineIndicator } from './InlineOfflineIndicator';
import { SystemAnnouncementBanner } from './SystemAnnouncementBanner';
import { HelpMenu } from '../HelpMenu';
import { Customer, Booking, Instructor } from '../../types/crm';
import { QuoteWithDetails } from '../../types/quotes';
import { supabaseCustomerStorage, supabaseBookingStorage, supabaseInstructorStorage } from '../../services/supabaseStorage';
import { QuotesService } from '../../services/quotesService';
import { supabase } from '../../lib/supabase';
import { DebugService } from '../../services/debugService';
import type { AppView } from '../../types/views';
import { prefersCrmWorkspace, setWorkspaceOverride } from '../../utils/workspacePreference';

interface DashboardLayoutProps {
  children: React.ReactNode;
  activeView: AppView;
  onViewChange: (view: AppView, context?: any) => void;
  canGoBack?: boolean;
  onGoBack?: () => void;
}

export function DashboardLayout({ children, activeView, onViewChange, canGoBack, onGoBack }: DashboardLayoutProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState({ minutes: 60, seconds: 0 });
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [quotes, setQuotes] = useState<QuoteWithDetails[]>([]);
  const { signOut, user } = useAuth();
  const { instructor, loading: instructorLoading } = useInstructor();
  const { interpreter, loading: interpreterLoading } = useInterpreter();
  const { customer: client, loading: clientLoading } = useClient();
  const { isSuperAdmin } = useSuperAdmin();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate('/thank-you');
  };

  const getUserDisplayName = () => {
    if (!user) return 'User';
    const firstName = user.user_metadata?.first_name || '';
    const lastName = user.user_metadata?.last_name || '';
    if (firstName && lastName) return `${firstName} ${lastName}`;
    if (firstName) return firstName;
    if (lastName) return lastName;
    const email = user.email || '';
    return email.split('@')[0] || 'User';
  };

  // Redirect clients to their portal page (PRIORITY - clients always take precedence over instructors)
  useEffect(() => {
    if (!user) return; // Don't redirect if user is logged out
    // Check client status first - if they're a client, redirect them regardless of instructor status
    if (!clientLoading && client) {
      const currentPath = window.location.pathname;
      const isClientPortalPath = currentPath === '/client-portal' || currentPath.startsWith('/client-portal/');
      const isPublicPath = currentPath.startsWith('/public-form') || 
                          currentPath.startsWith('/gdpr-consent') || 
                          currentPath.startsWith('/accept-quote') || 
                          currentPath.startsWith('/quote-accepted') ||
                          currentPath === '/thank-you';
      
      if (!isClientPortalPath && !isPublicPath) {
        // Generate a token for the client and redirect to token-based URL
        const generateTokenAndRedirect = async () => {
          try {
            const { ensureCustomerPortalToken } = await import('../../services/portalService');
            const link = await ensureCustomerPortalToken(client.id);
            if (link) {
              // Extract token from link (format: /client-portal/{token})
              const tokenMatch = link.match(/\/client-portal\/([^/?]+)/);
              if (tokenMatch && tokenMatch[1]) {
                // #region agent log
                fetch('http://127.0.0.1:7243/ingest/57dcde74-2a1a-4fcb-89a0-8bccddea619c',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardLayout.tsx:75',message:'Navigating to client portal with token',data:{targetPath:`/client-portal/${tokenMatch[1]}`},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
                // #endregion
                navigate(`/client-portal/${tokenMatch[1]}`);
              } else {
                // #region agent log
                fetch('http://127.0.0.1:7243/ingest/57dcde74-2a1a-4fcb-89a0-8bccddea619c',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardLayout.tsx:78',message:'Token extraction failed, using fallback',data:{targetPath:'/client-portal'},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
                // #endregion
                // Fallback to basic portal path if token extraction fails
                navigate('/client-portal');
              }
            } else {
              // #region agent log
              fetch('http://127.0.0.1:7243/ingest/57dcde74-2a1a-4fcb-89a0-8bccddea619c',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardLayout.tsx:82',message:'Token generation failed, using fallback',data:{targetPath:'/client-portal'},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
              // #endregion
              // Fallback if token generation fails
              navigate('/client-portal');
            }
          } catch (error) {
            console.error('Error generating portal token for client:', error);
            // #region agent log
            fetch('http://127.0.0.1:7243/ingest/57dcde74-2a1a-4fcb-89a0-8bccddea619c',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardLayout.tsx:87',message:'Error in token generation, using fallback',data:{error:error instanceof Error?error.message:String(error),targetPath:'/client-portal'},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
            // #endregion
            // Fallback to basic portal path
            navigate('/client-portal');
          }
        };
        
        generateTokenAndRedirect();
      }
    }
  }, [client, clientLoading, navigate, user]);

  // Redirect instructors to their assignments page (ONLY if they're NOT a client / CRM staff)
  useEffect(() => {
    if (!user) return; // Don't redirect if user is logged out
    // Dual-role CRM staff (same email as trainer) stay in the main CRM unless they switch
    if (prefersCrmWorkspace(user) || isSuperAdmin) return;
    // Only redirect instructors if they're NOT a client and both checks are done
    if (!instructorLoading && !clientLoading && instructor && !client && window.location.pathname !== '/instructor-assignments' && !window.location.pathname.startsWith('/public-form') && !window.location.pathname.startsWith('/gdpr-consent') && !window.location.pathname.startsWith('/accept-quote') && !window.location.pathname.startsWith('/quote-accepted') && !window.location.pathname.startsWith('/client-portal')) {
      navigate('/instructor-assignments');
    }
  }, [instructor, instructorLoading, client, clientLoading, navigate, user, isSuperAdmin]);

  // Redirect interpreters to their assignments page (if not client/instructor/CRM staff)
  useEffect(() => {
    if (!user) return;
    if (prefersCrmWorkspace(user) || isSuperAdmin) return;
    const currentPath = window.location.pathname;
    const isPublicPath = currentPath.startsWith('/public-form') ||
      currentPath.startsWith('/gdpr-consent') ||
      currentPath.startsWith('/accept-quote') ||
      currentPath.startsWith('/quote-accepted') ||
      currentPath.startsWith('/client-portal') ||
      currentPath === '/thank-you';

    if (
      !interpreterLoading &&
      !instructorLoading &&
      !clientLoading &&
      interpreter &&
      !instructor &&
      !client &&
      currentPath !== '/interpreter-assignments' &&
      !isPublicPath
    ) {
      navigate('/interpreter-assignments');
    }
  }, [interpreter, interpreterLoading, instructor, instructorLoading, client, clientLoading, navigate, user, isSuperAdmin]);

  const openTrainerPanel = () => {
    setWorkspaceOverride('instructor');
    navigate('/instructor-assignments');
  };

  const openInterpreterPanel = () => {
    setWorkspaceOverride('interpreter');
    navigate('/interpreter-assignments');
  };

  // Reset timer on any activity
  const resetTimer = () => {
    setTimeRemaining({ minutes: 60, seconds: 0 });
    setLastActivity(Date.now());
  };

  // Activity event handlers
  const handleActivity = () => {
    resetTimer();
  };

  // Set up activity listeners
  useEffect(() => {
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'focus'
    ];

    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, []);

  // Load notification data
  useEffect(() => {
    const loadNotificationData = async () => {
      try {
        const [customersData, bookingsData, instructorsData, quotesData] = await Promise.all([
          supabaseCustomerStorage.getCustomersForAdminNotifications(),
          supabaseBookingStorage.getBookingsForAdminNotifications(),
          supabaseInstructorStorage.getInstructors(),
          QuotesService.getQuotesWithDetails().catch(() => []) // Don't fail if quotes service fails
        ]);
        setCustomers(customersData);
        setBookings(bookingsData);
        setInstructors(instructorsData as Instructor[]);
        setQuotes(quotesData || []);
      } catch (error) {
        console.error('Error loading notification data:', error);
      }
    };

    // Load immediately on mount
    loadNotificationData();

    // Subscribe to quote changes via Supabase Realtime
    // This will automatically refresh quotes when they're updated (e.g., accepted)
    const quotesChannel = supabase
      .channel('quotes-notifications-realtime')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'quotes'
        },
        async () => {
          // When a quote is updated, reload quotes to get latest data
          try {
            const quotesData = await QuotesService.getQuotesWithDetails().catch(() => []);
            setQuotes(quotesData || []);
          } catch (error) {
            console.error('Error refreshing quotes for notifications:', error);
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('✅ Subscribed to quotes realtime updates for notifications');
        }
      });

    // Cleanup subscription on unmount
    return () => {
      supabase.removeChannel(quotesChannel);
    };
  }, []);

  // Auto-logout countdown timer (logs out after 60 minutes of inactivity)
  useEffect(() => {
    const updateTimer = () => {
      setTimeRemaining(prev => {
        if (prev.seconds > 0) {
          return { ...prev, seconds: prev.seconds - 1 };
        } else if (prev.minutes > 0) {
          return { minutes: prev.minutes - 1, seconds: 59 };
        } else {
          // Log out when timer reaches 0
          handleSignOut();
          return { minutes: 0, seconds: 0 };
        }
      });
    };

    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [signOut]);

  // Only show timer in last 5 minutes
  const shouldShowTimer = timeRemaining.minutes <= 5;
  const isWarning = timeRemaining.minutes <= 5;
  const isCritical = timeRemaining.minutes <= 2;

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 bg-pattern">
      {/* App chrome row: sidebar + main (mobile nav is fixed overlay, not a flex sibling) */}
      <div className="flex min-h-screen w-full">
        {/* Desktop Sidebar */}
        <div className="hidden lg:block">
          <ModernSidebar
            activeView={activeView}
            onViewChange={onViewChange}
            collapsed={sidebarCollapsed}
            onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          />
        </div>

        {/* Main Content Area — always full remaining width */}
        <div className={`flex min-w-0 flex-1 flex-col transition-all duration-300 ease-in-out ${
          sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-80'
        }`}>
          {/* System Announcements Banner */}
          <SystemAnnouncementBanner />
          
          {/* Desktop / tablet header — hidden on phone shell */}
          <div className="sticky top-0 z-40 hidden lg:block bg-[rgb(var(--surface-glass))]/95 backdrop-blur-sm border-b border-[rgb(var(--border))]/50 px-4 py-2.5 relative nav-enhanced">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2 lg:gap-4 min-w-0 flex-1">
                {canGoBack && onGoBack && (
                  <button
                    type="button"
                    onClick={onGoBack}
                    className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-[rgb(var(--border))] bg-[rgb(var(--surface))] px-2.5 py-1 text-xs font-medium text-[rgb(var(--foreground))] hover:bg-[rgb(var(--surface-alt))]"
                    title="Go back to previous view"
                  >
                    <ArrowLeft className="h-3.5 w-3.5" />
                    Back
                  </button>
                )}
                <Breadcrumbs activeView={activeView} />
                <div className="flex items-center gap-2 min-w-0 flex-1 max-w-3xl">
                  {activeView === 'dashboard' && user && (
                    <p className="hidden xl:block shrink-0 text-xs font-medium text-[rgb(var(--foreground))] truncate" title={`Welcome back, ${getUserDisplayName()}`}>
                      Welcome back,{' '}
                      <span className="text-blue-600 dark:text-blue-400 font-semibold">
                        {getUserDisplayName()}
                      </span>
                    </p>
                  )}
                  <div className="min-w-0 flex-1">
                    <GlobalSearch onViewChange={onViewChange} variant="inline" />
                  </div>
                </div>
              </div>
              <div className="relative z-10 flex shrink-0 flex-wrap items-center gap-2">
                {instructor && (
                  <button
                    type="button"
                    onClick={openTrainerPanel}
                    className="inline-flex items-center gap-1.5 px-2 py-1.5 sm:px-2.5 rounded-lg bg-[rgb(var(--surface))] border border-[rgb(var(--border))] hover:bg-[rgb(var(--surface-alt))] transition-colors text-xs font-medium text-[rgb(var(--foreground))]"
                    title="Open trainer panel"
                  >
                    <GraduationCap className="w-4 h-4" />
                    <span className="hidden xl:inline">Trainer panel</span>
                  </button>
                )}
                {interpreter && (
                  <button
                    type="button"
                    onClick={openInterpreterPanel}
                    className="inline-flex items-center gap-1.5 px-2 py-1.5 sm:px-2.5 rounded-lg bg-[rgb(var(--surface))] border border-[rgb(var(--border))] hover:bg-[rgb(var(--surface-alt))] transition-colors text-xs font-medium text-[rgb(var(--foreground))]"
                    title="Open interpreter panel"
                  >
                    <Languages className="w-4 h-4" />
                    <span className="hidden xl:inline">Interpreter panel</span>
                  </button>
                )}

                <button
                  onClick={toggleTheme}
                  className="p-2 rounded-lg bg-[rgb(var(--surface))] border border-[rgb(var(--border))] hover:bg-[rgb(var(--surface-alt))] transition-colors duration-200 flex items-center justify-center"
                  aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                  title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  {theme === 'dark' ? (
                    <Sun className="w-5 h-5 text-[rgb(var(--foreground))]" />
                  ) : (
                    <Moon className="w-5 h-5 text-[rgb(var(--foreground))]" />
                  )}
                </button>

                <div className="hidden xl:block">
                  <TimeWeatherWidget />
                </div>

                <InlineOfflineIndicator />

                <NotificationBell
                  customers={customers}
                  bookings={bookings}
                  instructors={instructors}
                  quotes={quotes}
                  isSuperAdmin={isSuperAdmin}
                  currentUser={user}
                  onTasksClick={() => onViewChange('tasks')}
                  onSuperAdminTabClick={(tab) => onViewChange('superadmin', { activeTab: tab })}
                  onNavigate={(url) => {
                    if (url.startsWith('/clients')) {
                      onViewChange('clients');
                    } else if (url.startsWith('/bookings')) {
                      onViewChange('bookings');
                    } else if (url.startsWith('/quotes')) {
                      onViewChange('quotes');
                    }
                  }}
                />
                
                {shouldShowTimer && (
                  <div className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                    isCritical 
                      ? 'bg-red-100 text-red-800 border border-red-200' 
                      : isWarning 
                        ? 'bg-amber-100 text-amber-800 border border-amber-200' 
                        : 'bg-blue-100 text-blue-800 border border-blue-200'
                  }`}>
                    {isWarning ? (
                      <AlertTriangle className="w-4 h-4" />
                    ) : (
                      <Clock className="w-4 h-4" />
                    )}
                    <span className="hidden sm:inline">
                      Session expires in {timeRemaining.minutes}:{timeRemaining.seconds.toString().padStart(2, '0')}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Page Content — pad for fixed mobile top bar + bottom tabs */}
          <div className="flex-1 overflow-auto">
            <div className="p-3 sm:p-4 lg:p-6 pt-[calc(3.75rem+env(safe-area-inset-top))] lg:pt-6 pb-[calc(5.5rem+env(safe-area-inset-bottom))] lg:pb-6">
              {children}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile chrome: fixed overlays — must not sit in the flex row */}
      <div className="lg:hidden">
        <MobileNavigation
          activeView={activeView}
          onViewChange={onViewChange}
          isOpen={mobileMenuOpen}
          onToggle={setMobileMenuOpen}
          canGoBack={canGoBack}
          onGoBack={onGoBack}
          customers={customers}
          bookings={bookings}
          instructors={instructors}
          quotes={quotes}
        />
      </div>

      {/* Animated background elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none -z-10">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/5 to-indigo-400/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-emerald-400/5 to-teal-400/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <HelpMenu activeView={activeView} onViewChange={onViewChange} />
    </div>
  );
}